import { faArrowDown, faArrowRight, faArrowUp, faCopy, faEdit, faEllipsisV, faObjectUngroup, faTrash } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Dropdown, Flex, Space, Typography } from "antd"
import dayjs from "dayjs"
import { CSSProperties, useContext, useState } from "react"
import { useCalendarEventsForJob } from "../../../../domain/calendar/calendEventQueries"
import { Workarea } from "../../../../domain/job"
import { WorkareaGroup } from "../../../../domain/workareas/group"
import { roundToPrecision } from "../../../../helpers/math"
import { hasValue } from "../../../../helpers/nullable"
import { pluralize } from "../../../../helpers/strings"
import { ItemsContext } from "../../../Items/ItemsRoot"
import VSpace from "../../../common/VSpace"
import { centsToDollarsDisplay } from "../../../displayFormatters"
import DeleteWorkareaModal from "../EditorDrawer/Sidebar/DeleteWorkareaModal"
import DuplicateWorkareaModal from "../EditorDrawer/Sidebar/DuplicateWorkareaModal"
import JobPlanContext from "../JobPlanContext"
import ApprovedOrOnHoldDisplay from "./ApprovedOrOnHoldDisplay"
import Grabber from "./Grabber"
import PriceSummary from "./PriceSummary"
import PricingTypeDisplay from "../../PricingTypeDisplay"
import Sortable from "./Sortable"
import { tagWorkarea } from "./dndModel"
import { countChildRecurringWorkareas, GroupedWorkareas } from "./workareaGroupsListModel"

const { Text } = Typography

const workareaWrapperStyle: CSSProperties = {
  paddingLeft: 56,
}

const workareaCardStyle: CSSProperties = {
  border: '1px solid #efefef',
  backgroundColor: '#fff',
  borderRadius: 4,
  padding: 16,
  color: '#1e1e1e'
}

const onHoldStyle: CSSProperties = {
  backgroundColor: '#fff1f0'
}

type SummaryProps = {
  type: string
  hours: number
  totalPrice: number
}
export const Summary = ({ type, hours, totalPrice }: SummaryProps) => {
  return <Space direction="vertical" size="small">
    <span>
      {roundToPrecision(hours, 1)} {pluralize(hours, 'Hour')} /&nbsp;
      {centsToDollarsDisplay(totalPrice)}
    </span>
    <Text type="secondary"><small>{type}</small></Text>
  </Space>
}

type RecurringSummaryProps = {
  jobId: number,
  workareaId: number
  estimatedOccurrences: number
}
export const RecurringSummary = ({ estimatedOccurrences, jobId, workareaId }: RecurringSummaryProps) => {
  const calendarEventsQuery = useCalendarEventsForJob(jobId, hasValue(estimatedOccurrences))
  const calendarEvents = (calendarEventsQuery.data ?? []).filter(x => x.workareaId === workareaId)
  let scheduleRangeStart: string | undefined
  let scheduleRangeEnd: string | undefined

  if (calendarEvents.length > 0) {
    const startDates = calendarEvents.map(x => x.startUtc)
    startDates.sort((a, b) => a.localeCompare(b))
    scheduleRangeStart = dayjs(startDates[0]).format('MMM D, YYYY')
    scheduleRangeEnd = dayjs(startDates[startDates.length - 1]).format('MMM D, YYYY')
  }

  return <Space direction="vertical" size="small">
    {
      (estimatedOccurrences > 0 && estimatedOccurrences) && <>
        <span>
          {estimatedOccurrences} {pluralize(estimatedOccurrences, "visit")}
        </span>
        <Text type="secondary">
          <small>
            {hasValue(scheduleRangeStart) && <>
              {scheduleRangeStart}
              {hasValue(scheduleRangeEnd) && scheduleRangeStart !== scheduleRangeEnd && <>&mdash;{scheduleRangeEnd}</>}
            </>}
            {!hasValue(scheduleRangeStart) && <>Unscheduled</>}
          </small>
        </Text>
      </>
    }
  </Space>
}

type WorkareaComponentProps = {
  workarea: Workarea
  workareaGroup: WorkareaGroup
  groupedWorkareas?: GroupedWorkareas
  onMoveUp: () => void
  onMoveDown: () => void
  onMoveTo: () => void
  onRemoveFromGroup: () => void
}
export const WorkareaComponent = ({
  workarea,
  workareaGroup,
  groupedWorkareas,
  onMoveDown,
  onMoveUp,
  onMoveTo,
  onRemoveFromGroup,
}: WorkareaComponentProps) => {
  const { workareaSummaries, jobData: { jobId } } = useContext(ItemsContext)
  const { selectWorkarea } = useContext(JobPlanContext)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)

  const workareaSummary = workareaSummaries.find(s => s.workareaId === workarea.id)

  const calendarEventsQuery = useCalendarEventsForJob(jobId, true)
  const scheduleDates = (calendarEventsQuery.data ?? []).filter(x => x.workareaId === workarea.id).map(x => x.startUtc)
  scheduleDates.sort((a, b) => a.localeCompare(b))

  return <div style={{ ...workareaCardStyle, ...(workareaGroup.isOnHold ? onHoldStyle : {}) }}>
    <Flex>
      <Grabber />
      <Flex justify="space-between" align="center" onClick={() => selectWorkarea(workarea.id)} style={{ cursor: 'pointer', width: '100%' }}>
        <Space size="large">
          <VSpace>
            <Space onClick={() => selectWorkarea(workarea.id)} style={{ cursor: 'pointer' }}>
              <Text strong delete={workarea.isOnHold}>{workarea.name} </Text>
              {scheduleDates.length > 0 && !hasValue(workarea.estimatedOccurrences) && dayjs(scheduleDates[0]).format('MMM D, YYYY')}
            </Space>
            {workareaGroup.isHidden &&
              <Space>
                <Text type="secondary">
                  <PricingTypeDisplay pricingType={workareaGroup.pricingType} />
                </Text>
                <ApprovedOrOnHoldDisplay workareaGroup={workareaGroup} />
              </Space>
            }
          </VSpace>
        </Space>
        <Space size="large">
          {hasValue(workarea.estimatedOccurrences) &&
            <RecurringSummary
              estimatedOccurrences={workarea.estimatedOccurrences}
              jobId={jobId}
              workareaId={workarea.id}
            />}
          {workareaSummary &&
            <PriceSummary
              finalPrice={workareaSummary.finalPrice}
              estimatedPrice={workareaSummary.estimatedPrice}
              estimatedHours={workareaSummary.estimatedHours}
              estimatedHoursPerVisit={workareaSummary.estimatedHoursPerOccurrence}
              estimatedPricePerVisit={workareaSummary.estimatedPricePerOccurrence}
              pricingType={workareaGroup.pricingType}
              customQuotedPrice={workarea.customQuotedPrice}
            />
          }
          <Button icon={<FontAwesomeIcon icon={faEdit} />} onClick={() => selectWorkarea(workarea.id)}>Edit</Button>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  key: 'duplicate',
                  label: <Space><FontAwesomeIcon icon={faCopy} />Duplicate</Space>,
                  onClick: (e) => {
                    e.domEvent.stopPropagation()
                    setIsDuplicateModalOpen(true)
                  }
                },
                {
                  key: 'delete',
                  label: <Space><FontAwesomeIcon icon={faTrash} />Delete</Space>,
                  onClick: (e) => {
                    e.domEvent.stopPropagation()
                    setIsDeleteModalOpen(true)
                  }
                },
                {
                  key: 'move-up',
                  label: <Space><FontAwesomeIcon icon={faArrowUp} />Move Up</Space>,
                  onClick: (e) => {
                    e.domEvent.stopPropagation()
                    onMoveUp()
                  }
                },
                {
                  key: 'move-down',
                  label: <Space><FontAwesomeIcon icon={faArrowDown} />Move Down</Space>,
                  onClick: (e) => {
                    e.domEvent.stopPropagation()
                    onMoveDown()
                  }
                },
                {
                  key: 'move-to',
                  label: <Space><FontAwesomeIcon icon={faArrowRight} />Move To&hellip;</Space>,
                  onClick: (e) => {
                    e.domEvent.stopPropagation()
                    onMoveTo()
                  }
                },
                ...(workareaGroup.isHidden ? [] : [{
                  key: 'remove-from-group',
                  label: <Space><FontAwesomeIcon icon={faObjectUngroup} />Remove from Group</Space>,
                  onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                    e.domEvent.stopPropagation()
                    onRemoveFromGroup()
                  }
                }])
              ]
            }}
          >
            <Button 
              type="text" 
              icon={<FontAwesomeIcon icon={faEllipsisV} />} 
              onClick={e => e.stopPropagation()}
            />
          </Dropdown>
        </Space>
      </Flex>
    </Flex>

    <DeleteWorkareaModal
      isOpen={isDeleteModalOpen}
      jobId={jobId}
      onCancel={() => setIsDeleteModalOpen(false)}
      workareaId={workarea.id}
      workareaName={workarea.name}
      isWorkareaGroupApproved={workareaGroup.isApproved}
      hasChildRecurringWorkareas={countChildRecurringWorkareas(groupedWorkareas, workarea.id) > 0}
    />
    <DuplicateWorkareaModal
      isOpen={isDuplicateModalOpen}
      onCancel={() => setIsDuplicateModalOpen(false)}
      workarea={workarea}
    />
  </div>
}

export const IndentedWorkareaComponent = (props: WorkareaComponentProps) => {
  return <div style={workareaWrapperStyle}>
    <WorkareaComponent {...props} />
  </div>
}

const groupLineStyle: CSSProperties = {
  position: 'absolute',
  left: '24px',
  width: '30px',
  height: '100%',
  borderLeft: '2px solid #e9e9e9',
}

export const GroupLineConnector = () => <div style={{ ...groupLineStyle, height: '56px', borderBottom: '2px solid #e9e9e9' }}></div>
const GroupLineExtender = () => <div style={groupLineStyle}></div>

export const SortableWorkareaComponent = (
  {
    workarea,
    workareaGroup,
    groupedWorkareas,
    isLast,
    onMoveUp,
    onMoveDown,
    onMoveTo,
    onRemoveFromGroup
  }: {
    workarea: Workarea,
    workareaGroup: WorkareaGroup,
    groupedWorkareas?: GroupedWorkareas,
    isLast?: boolean
    onMoveUp: () => void
    onMoveDown: () => void
    onMoveTo: () => void
    onRemoveFromGroup: () => void
  }
) => {
  return <Sortable
    id={workarea.id}
    data={tagWorkarea(workarea)}
    activeStyle={{ visibility: 'hidden' }}
  >
    <div style={{ position: 'relative' }}>
      <GroupLineConnector />
      {!isLast && <GroupLineExtender />}
      <div style={{ height: '16px' }}></div>
      <IndentedWorkareaComponent
        workarea={workarea}
        workareaGroup={workareaGroup}
        groupedWorkareas={groupedWorkareas}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        onMoveTo={onMoveTo}
        onRemoveFromGroup={onRemoveFromGroup}
      />
    </div>
  </Sortable>
}
